import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency } from "@/lib/calc";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, Target, Wallet, ArrowRight, TrendingUp, ListChecks, Check } from "lucide-react";
import { ACCENTS, BRAND } from "@/lib/palette";

const Stat = ({ icon: Icon, label, value, accent, testId }) => (
  <Card className="rounded-2xl border bg-card p-5 shadow-sm">
    <div className="flex items-center gap-2 text-muted-foreground mb-2">
      <Icon className="h-4 w-4" style={{ color: accent }} />
      <span className="font-mono-label text-[11px] uppercase tracking-wider">{label}</span>
    </div>
    <div className="font-heading font-bold text-2xl sm:text-3xl tabular-nums" data-testid={testId}>{value}</div>
  </Card>
);

export default function StaffDashboard() {
  const { business, user } = useAuth();
  const cur = business?.currency || "AUD";
  const [me, setMe] = useState(null);
  const [myTasks, setMyTasks] = useState([]);

  const loadTasks = () => api.get("/crm/my-tasks").then(({ data }) => setMyTasks(data)).catch(() => {});
  useEffect(() => { api.get("/dashboard/me").then(({ data }) => setMe(data)).catch(() => {}); loadTasks(); }, []);
  const toggleMine = async (t) => { try { await api.patch(`/crm/tasks/${t.id}`, { status: t.status === "open" ? "done" : "open" }); loadTasks(); } catch { /* ignore */ } };

  const pct = me ? Math.min(me.kpi_pct, 100) : 0;
  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Hey {firstName} 👋</h1>
          <p className="text-muted-foreground mt-1">Here's how your month is tracking</p>
        </div>
        <Link to="/calendar"><Button className="rounded-lg" data-testid="staff-open-calendar">My calendar <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
      </div>

      {!me ? (
        <div className="text-muted-foreground py-16 text-center">Loading…</div>
      ) : (
        <>
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            {/* KPI progress */}
            <Card className="rounded-2xl border bg-card p-5 shadow-sm lg:col-span-2" data-testid="staff-kpi-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Target className="h-4 w-4 text-primary" />
                  <span className="font-mono-label text-[11px] uppercase tracking-wider">Your target this month</span>
                </div>
                <span className="font-heading font-bold text-lg tabular-nums" data-testid="staff-kpi-pct">{Math.round(me.kpi_pct)}%</span>
              </div>
              <div className="h-3 rounded-full bg-muted overflow-hidden mb-3">
                <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: me.kpi_pct >= 100 ? BRAND.success : (me.color || BRAND.primary) }} />
              </div>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-muted-foreground">Sales so far</span>
                <span className="font-heading font-bold text-xl tabular-nums" data-testid="staff-sales">{fmtCurrency(me.sales_month, cur, 0)}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="rounded-xl bg-muted/40 px-3 py-2" data-testid="staff-services"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Services</div><div className="font-heading font-bold tabular-nums">{fmtCurrency(me.services_month || 0, cur, 0)}</div></div>
                <div className="rounded-xl bg-muted/40 px-3 py-2" data-testid="staff-retail"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Retail</div><div className="font-heading font-bold tabular-nums">{fmtCurrency(me.retail_month || 0, cur, 0)}</div></div>
              </div>
              <div className="flex items-baseline justify-between text-sm mt-2">
                <span className="text-muted-foreground">KPI target so far</span>
                <span className="tabular-nums text-muted-foreground" data-testid="staff-target">{fmtCurrency(me.target, cur, 0)}</span>
              </div>
            </Card>

            {/* profit share */}
            <Card className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="staff-profit-share">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Wallet className="h-4 w-4 text-primary" />
                <span className="font-mono-label text-[11px] uppercase tracking-wider">Profit share</span>
              </div>
              {me.profit_share_enabled ? (
                <>
                  <div className="font-heading font-bold text-2xl tabular-nums" data-testid="staff-ps-earned">{fmtCurrency(me.profit_share_earned, cur, 0)}</div>
                  <div className="text-xs text-muted-foreground mt-1">{me.profit_share_pct}% over {fmtCurrency(me.profit_share_threshold, cur, 0)} in sales</div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">Not enabled on your profile. Ask your manager if you're eligible.</div>
              )}
            </Card>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <Stat icon={CalendarDays} label="Bookings Today" accent={ACCENTS.one} value={me.today_bookings} testId="staff-today-bookings" />
            <Stat icon={TrendingUp} label="Upcoming" accent={ACCENTS.two} value={me.upcoming_count} testId="staff-upcoming" />
            <Stat icon={CalendarDays} label="Bookings (Month)" accent={ACCENTS.four} value={me.bookings_month} testId="staff-month-bookings" />
          </div>

          <Card className="rounded-2xl border bg-card p-5 shadow-sm mb-6" data-testid="staff-followups">
            <div className="flex items-center gap-2 text-muted-foreground mb-3">
              <ListChecks className="h-4 w-4 text-primary" />
              <span className="font-mono-label text-[11px] uppercase tracking-wider">My follow-ups</span>
            </div>
            {!myTasks.length ? (
              <p className="text-sm text-muted-foreground" data-testid="staff-followups-empty">Nothing assigned to you right now. 🎉</p>
            ) : (
              <div className="divide-y">
                {myTasks.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 py-2.5" data-testid={`staff-task-${t.id}`}>
                    <button onClick={() => toggleMine(t)} className={`h-5 w-5 rounded border flex items-center justify-center shrink-0 ${t.status === "done" ? "bg-primary border-primary text-white" : "border-neutral-300"}`} data-testid={`staff-task-toggle-${t.id}`}>
                      {t.status === "done" && <Check className="h-3.5 w-3.5" />}
                    </button>
                    <div className={`flex-1 text-sm ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>
                      {t.title}{t.client_name ? <span className="text-muted-foreground"> · {t.client_name}</span> : ""}
                      {t.due_date && <span className="text-xs text-muted-foreground ml-2">due {t.due_date}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

        </>
      )}
    </div>
  );
}
